<template>
  <div class="page-header">
    <h2>Paramètres de l'application</h2>
  </div>

  <InfoBox
    v-if="infoMessage"
    v-model:open="infoOpen"
    :type="infoType"
    @close="infoOpen = false; infoMessage = ''"
  >
    {{ infoMessage }}
  </InfoBox>

  <div v-if="!loading && settings" class="settings-layout">

    <!-- Colonne principale : Général (2/3) -->
    <div class="card card-admin" :class="{ 'is-modified': isDirtySection('general') }">
      <div class="card-content">
        <h3>🌐 Général</h3>

        <div class="form-group">
          <label>Nom de l'application</label>
          <input
            v-model="form.appName"
            type="text"
            placeholder="EasyGO"
            @input="markDirty('general')"
          />
        </div>

        <div class="form-group">
          <label>Email de contact</label>
          <input
            v-model="form.contactEmail"
            type="email"
            placeholder="contact@easygo.fr"
            @input="markDirty('general')"
          />
        </div>

        <div class="form-group">
          <div class="form-group">
            <label>Résultats par page</label>
            <input
              v-model.number="form.itemsPerPage"
              type="number"
              min="5"
              max="200"
              @input="markDirty('general')"
            />
          </div>
          <div class="form-group">
            <label>Tentatives de connexion max</label>
            <input
              v-model.number="form.maxLoginAttempts"
              type="number"
              min="1"
              max="20"
              @input="markDirty('general')"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Pays par défaut</label>
          <select v-model="form.defaultCountryId" @change="markDirty('general')">
            <option :value="null">-- Sélectionner un pays --</option>
            <option v-for="country in countries" :key="country.id" :value="country.id">
              {{ country.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label>Texte du footer</label>
          <textarea
            v-model="form.footerText"
            rows="3"
            placeholder="© 2025 EasyGO. Tous droits réservés."
            @input="markDirty('general')"
          />
        </div>
      </div>

      <div class="card-footer">
        <button
          @click="saveSection('general')"
          class="btn btn-success"
          :class="{ 'is-highlight': isDirtySection('general') }"
          :disabled="!isDirtySection('general') || saving"
        >
          💾 Enregistrer
        </button>
        <button
          @click="resetSection('general')"
          class="btn btn-secondary"
          :disabled="!isDirtySection('general')"
        >
          Annuler
        </button>
      </div>
    </div>

    <!-- Colonne aside (1/3) : Notifications + Maintenance -->
    <aside class="settings-aside">

      <!-- Notifications -->
      <div class="card card-admin" :class="{ 'is-modified': isDirtySection('aside') }">
        <div class="card-content">
          <h3>🔔 Notifications & Maintenance</h3>

          <div class="form-group">
            <label>Notifications par email</label>
            <button
              type="button"
              class="toggle"
              @click="toggleAndSave('emailNotificationsEnabled')"
            >
              <span class="toggle-track" :class="{ 'is-active': form.emailNotificationsEnabled }">
                <span class="toggle-thumb" :class="{ 'is-active': form.emailNotificationsEnabled }"></span>
              </span>
            </button>
          </div>

          <div class="form-group">
            <label>Notifications par SMS</label>
            <button
              type="button"
              class="toggle"
              @click="toggleAndSave('smsNotificationsEnabled')"
            >
              <span class="toggle-track" :class="{ 'is-active': form.smsNotificationsEnabled }">
                <span class="toggle-thumb" :class="{ 'is-active': form.smsNotificationsEnabled }"></span>
              </span>
            </button>
          </div>

          <div class="form-group">
            <label>Mode maintenance</label>
            <button
              type="button"
              class="toggle"
              @click="toggleMaintenance"
            >
              <span class="toggle-track" :class="{ 'is-active': form.maintenanceMode }">
                <span class="toggle-thumb" :class="{ 'is-active': form.maintenanceMode }"></span>
              </span>
            </button>
          </div>

          <InfoBox v-if="form.maintenanceMode" type="warning" :closable="false">
            ⚠️ L'application est inaccessible aux utilisateurs.
          </InfoBox>
        </div>
      </div>

    </aside>

  </div>

  <EmptyState
    v-if="!loading && !settings"
    :is-empty="true"
    emptyMessage="Impossible de charger les paramètres"
  />
</template>

<script setup>
import { config } from '@/config/env.js'
import { ref, reactive, computed, onMounted } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useModalStore } from '@/stores/modalStore'
import InfoBox from '@/components/InfoBox.vue'
import EmptyState from '@/components/EmptyState.vue'
import { useSettingsStore } from '@/stores/settingsStore'

const settingsStore = useSettingsStore()

const emit = defineEmits(['loading'])
const toast = useToast()
const modalStore = useModalStore()

const infoOpen = ref(false)
const infoType = ref('info')
const infoMessage = ref('')

const loading = ref(false)
const saving = ref(false)
const settings = ref(null)
const countries = ref([])

const dirtySections = ref(new Set())

const form = reactive({
  appName: '',
  contactEmail: '',
  defaultCountryId: null,
  itemsPerPage: 20,
  maxLoginAttempts: 5,
  footerText: '',
  emailNotificationsEnabled: true,
  smsNotificationsEnabled: false,
  maintenanceMode: false,
})

const sectionFields = {
  general: ['appName', 'contactEmail', 'defaultCountryId', 'itemsPerPage', 'maxLoginAttempts', 'footerText'],
  aside:   ['emailNotificationsEnabled', 'smsNotificationsEnabled', 'maintenanceMode'],
}

const selectedCountry = computed(() =>
  countries.value.find(c => c.id === form.defaultCountryId) ?? null
)

const markDirty = (section) => dirtySections.value.add(section)
const isDirtySection = (section) => dirtySections.value.has(section)

const applySettings = (data) => {
  settings.value = { ...data }
  Object.keys(form).forEach(key => {
    if (key in data) form[key] = data[key]
  })
  settingsStore.updateSettings(data)  // ← sync le store global
}

const resetSection = (section) => {
  if (!settings.value) return
  sectionFields[section].forEach(key => { form[key] = settings.value[key] })
  dirtySections.value.delete(section)
}

// Toggle + sauvegarde immédiate pour les booléens de l'aside
const toggleAndSave = async (field) => {
  form[field] = !form[field]
  await patchFields({ [field]: form[field] })
}

// Toggle maintenance avec confirmation modale avant activation
const toggleMaintenance = async () => {
  const newValue = !form.maintenanceMode

  if (newValue) {
    const { result } = modalStore.open({
      kind: 'confirm',
      title: 'Activer le mode maintenance',
      message: "L'application sera inaccessible aux utilisateurs. Confirmer ?",
      confirmText: 'Activer',
      cancelText: 'Annuler'
    })
    const res = await result
    if (res.action !== 'confirm') return
  }

  form.maintenanceMode = newValue
  await patchFields({ maintenanceMode: newValue })
}

// PATCH générique — utilisé par toggleAndSave ET saveSection
const patchFields = async (payload) => {
  saving.value = true
  try {
    const res = await fetch(config.apiUrl + '/settings', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    })

    if (res.ok) {
      applySettings(await res.json())
      toast.success('Paramètres enregistrés')
      showInfo('success', 'Paramètres enregistrés avec succès')
    } else {
      const errData = await res.json()
      showInfo('error', errData.error || 'Erreur lors de la sauvegarde')
      toast.error(errData.error || 'Erreur lors de la sauvegarde')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
  } finally {
    saving.value = false
  }
}

const saveSection = async (section) => {
  const payload = {}
  sectionFields[section].forEach(key => { payload[key] = form[key] })
  await patchFields(payload)
  dirtySections.value.delete(section)
}

onMounted(async () => {
  await Promise.all([loadCountries(), loadSettings()])
})

const loadCountries = async () => {
  try {
    const res = await fetch(config.apiUrl + '/countries', { credentials: 'include' })
    if (res.ok) countries.value = await res.json()
    else toast.error('Erreur lors du chargement des pays')
  } catch (err) {
    toast.error(err?.message || 'Erreur de connexion')
  }
}

const loadSettings = async () => {
  loading.value = true
  emit('loading', true)
  try {
    const res = await fetch(config.apiUrl + '/settings', { credentials: 'include' })
    if (res.ok) {
      applySettings(await res.json())
    } else {
      showInfo('error', 'Erreur lors du chargement des paramètres')
      toast.error('Erreur lors du chargement des paramètres')
    }
  } catch (err) {
    showInfo('error', err?.message || 'Erreur de connexion')
    toast.error('Erreur de connexion')
  } finally {
    loading.value = false
    emit('loading', false)
  }
}

const showInfo = (type, message) => {
  infoType.value = type
  infoMessage.value = message
  infoOpen.value = true
}
</script>

<style scoped>
.settings-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

.settings-aside {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.card-footer {
  padding: 1.5rem 0;
}

@media (max-width: 768px) {
  .settings-layout {
    grid-template-columns: 1fr;
  }
}
</style>/style>
